// src/components/MapSharedUtils.js
import { useState, useEffect } from 'react';
import { TOOLTIP_STYLE } from './UI.jsx';

/**
 * Returns a single color based on the value, handling both Gradient and Categorical logic.
 */
export const getSingleColor = (val, isGradient, stats, stableColorConfig) => {
  if (isGradient && stats?.isNumeric) {
    const parsedVal = parseFloat(val);
    if (!isNaN(parsedVal)) {
      const p = (parsedVal - stats.min) / (stats.max - stats.min || 1);
      return [255, 255 * (1 - p), 0, 200];
    }
    return [150, 150, 150, 10]; // Fallback for invalid numeric
  }
  return stableColorConfig?.colorMap.get(val) || [150, 150, 150, 10];
};

/**
 * Returns an averaged color for a cluster of values.
 */
export const getMixedColor = (values, isGradient, stats, stableColorConfig) => {
  if (!values || values.length === 0) return [150, 150, 150, 10];

  if (isGradient && stats?.isNumeric) {
    const numericValues = values.map(v => parseFloat(v)).filter(v => !isNaN(v));
    if (numericValues.length > 0) {
      const avg = numericValues.reduce((a, b) => a + b, 0) / numericValues.length;
      const p = (avg - stats.min) / (stats.max - stats.min || 1);
      return [255, 255 * (1 - p), 0, 200];
    }
    return [150, 150, 150, 10];
  } else {
    let r = 0, g = 0, b = 0, a = 0;
    values.forEach(val => {
      const col = stableColorConfig?.colorMap.get(val) || [150, 150, 150, 10];
      r += col[0]; g += col[1]; b += col[2]; a += (col[3] ?? 255);
    });
    const count = values.length;
    return [r / count, g / count, b / count, a / count];
  }
};

export const getSingleColorMultigradient = (val, isGradient, stats, stableColorConfig) => {
  if (isGradient && stats?.isNumeric) {
    const parsedVal = parseFloat(val);
    if (!isNaN(parsedVal)) {
      const t = Math.max(0, Math.min(1, (parsedVal - stats.min) / (stats.max - stats.min || 1)));
      
      const stops = [
        { pos: 0.0, r: 0,   g: 0,   b: 255 }, // Blue
        { pos: 0.25,r: 0,   g: 255, b: 0   }, // Green
        { pos: 0.5, r: 255, g: 255, b: 255 }, // White
        { pos: 0.75,r: 255, g: 255, b: 0   }, // Yellow
        { pos: 1.0, r: 255, g: 0,   b: 0   }  // Red
      ];
      
      let lower = stops[0];
      let upper = stops[stops.length - 1];
      
      for (let i = 0; i < stops.length - 1; i++) {
        if (t >= stops[i].pos && t <= stops[i+1].pos) {
          lower = stops[i];
          upper = stops[i+1];
          break;
        }
      }
      
      const range = upper.pos - lower.pos || 1;
      const factor = (t - lower.pos) / range;
      
      return [
        Math.round(lower.r + factor * (upper.r - lower.r)),
        Math.round(lower.g + factor * (upper.g - lower.g)),
        Math.round(lower.b + factor * (upper.b - lower.b)),
        200
      ];
    }
    return [150, 150, 150, 10]; // Fallback for invalid numeric
  }
  return stableColorConfig?.colorMap.get(val) || [150, 150, 150, 10];
};

/**
 * Returns an averaged color for a cluster of values.
 */
export const getMixedColorMultigradient = (values, isGradient, stats, stableColorConfig) => {
  if (!values || values.length === 0) return [150, 150, 150, 10];

  if (isGradient && stats?.isNumeric) {
    const numericValues = values.map(v => parseFloat(v)).filter(v => !isNaN(v));
    if (numericValues.length > 0) {
      const avg = numericValues.reduce((a, b) => a + b, 0) / numericValues.length;
      const t = Math.max(0, Math.min(1, (avg - stats.min) / (stats.max - stats.min || 1)));
      
      const stops = [
        { pos: 0.0, r: 0,   g: 0,   b: 255 }, // Blue
        { pos: 0.25,r: 0,   g: 255, b: 0   }, // Green
        { pos: 0.5, r: 255, g: 255, b: 255 }, // White
        { pos: 0.75,r: 255, g: 255, b: 0   }, // Yellow
        { pos: 1.0, r: 255, g: 0,   b: 0   }  // Red
      ];
      
      let lower = stops[0];
      let upper = stops[stops.length - 1];
      
      for (let i = 0; i < stops.length - 1; i++) {
        if (t >= stops[i].pos && t <= stops[i+1].pos) {
          lower = stops[i];
          upper = stops[i+1];
          break;
        }
      }
      
      const range = upper.pos - lower.pos || 1;
      const factor = (t - lower.pos) / range;
      
      return [
        Math.round(lower.r + factor * (upper.r - lower.r)),
        Math.round(lower.g + factor * (upper.g - lower.g)),
        Math.round(lower.b + factor * (upper.b - lower.b)),
        200
      ];
    }
    return [150, 150, 150, 10];
  } else {
    let r = 0, g = 0, b = 0, a = 0;
    values.forEach(val => {
      const col = stableColorConfig?.colorMap.get(val) || [150, 150, 150, 10];
      r += col[0]; g += col[1]; b += col[2]; a += (col[3] ?? 255);
    });
    const count = values.length;
    return [r / count, g / count, b / count, a / count];
  }
};

/**
 * Shared DeckGL Click Handler for Points/Clusters
 */
export const handleSharedPointClick = (info, lookupData, onSampleClick, setExpandedId) => {
  if (!lookupData || info.index === -1) return;
  const object = lookupData[info.index];
  if (!object) return;

  if (object.count === 1 && object.allSamples?.length > 0) {
    if (onSampleClick) onSampleClick(object.allSamples[0].index);
    if (setExpandedId) setExpandedId(null); // Collapse any previously expanded points
  } else if (object.isChild && object.sampleIndex !== undefined) {
    if (onSampleClick) onSampleClick(object.sampleIndex);
  } else {
    const pointId = object.pointId || object.id || `${object.position[0]}-${object.position[1]}`;
    setExpandedId(prev => prev === pointId ? null : pointId);
  }
};

/**
 * Shared Tooltip Generator for all map types
 */
export const getSharedTooltip = (info, lookupData, expandedId, colorBy) => {
  const { object, index, layer } = info;
  if (!layer || (!object && index < 0)) return null;

  // 1. GeoJSON Feature
  if (object && object.properties) {
    return {
      html: `
        <div class="text-xs p-1">
          <div class="font-bold border-b border-gray-300 mb-1">Feature Info</div>
          <strong>${colorBy}:</strong> ${object.properties[colorBy] || 'N/A'}
        </div>
      `,
      style: TOOLTIP_STYLE
    };
  }

  // 2. Scatterplot Point
  const item = lookupData ? lookupData[index] : null;
  if (!item) return null;

  if (item.isChild) {
    return {
      html: `<div class="text-xs p-1"><div class="font-bold border-b border-gray-300 mb-1">Sample Details</div><strong>${colorBy}:</strong> ${item.displayVal}</div>`,
      style: TOOLTIP_STYLE
    };
  }

  const isExpanded = expandedId === item.pointId;
  if (item.isCenter && isExpanded && item.count > 1) {
    return {
      html: `<div class="text-xs p-0.5 text-center"><div class="font-bold text-gray-500">Click to Collapse</div><div class="text-gray-500">${item.count} samples at this location</div></div>`,
      style: TOOLTIP_STYLE
    };
  }

  const vals = Array.from(item.uniqueValues || []);
  const firstVals = vals.slice(0, 5).map(v => (typeof v === 'string' && v.length > 36) ? v.slice(0, 18) + '...' + v.slice(-18) : v);
  const displayVals = firstVals.join(', ');
  const suffix = vals.length > 5 ? " ..." : "";

  return {
    html: `
      <div class="text-xs p-1">
        <div class="font-bold border-b border-gray-300 mb-1">Location Info</div>
        <div class="flex">
          <strong class="mr-1">Count:</strong> ${item.count} 
          ${item.count > 1 ? '<div class="text-gray-500 font-semibold mb-1 ml-4">(Click to Expand)</div>' : ''}
        </div>
        <strong>${colorBy}:</strong> ${displayVals}${suffix}
      </div>
    `,
    style: TOOLTIP_STYLE
  };
};

/**
 * Hook to fetch remote GeoJSON (used by Robinson and Globe for landmass data)
 */
export const useRemoteGeoJson = (url) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(url).then(res => res.json()).then(setData).catch(console.error);
  }, [url]);
  return data;
};